'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { scheduleConsentReplay } from '../lib/consentReplay';
import {
  ACCEPTED_CONSENT,
  applyConsent,
  clearConsentCookies,
  getStoredConsent,
  REJECTED_CONSENT,
  saveConsent,
} from '../lib/cookieConsent';

const CookieConsentContext = createContext(null);

// Повтаряме триенето на бисквитки след оттегляне — хваща таг, който ги пренаписва
// асинхронно, след като revoke сигналът вече е тръгнал.
const SWEEP_RETRY_BASE_MS = 1000;
const COOKIE_SWEEP_RETRY_DELAYS_MS = [SWEEP_RETRY_BASE_MS, SWEEP_RETRY_BASE_MS * 3];

/**
 * Управлява cookie consent-а: чете записания избор, показва банер при първо
 * посещение, прилага избора към Google Consent Mode v2 + GTM dataLayer.
 * Default-ите към Google се задават inline в <head>, преди GTM.
 */
export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(null); // null → още няма избор (показва банер)
  const [ready, setReady] = useState(false); // за да няма hydration mismatch
  const [settingsOpen, setSettingsOpen] = useState(false);
  const sweepTimersRef = useRef([]);

  // `setConsent`/`setReady` НЕ се отлагат — от тях зависи дали се показва банерът.
  // Преиграването към GTM обаче чака свободен момент; защо — виж consentReplay.js.
  useEffect(() => {
    const stored = getStoredConsent();
    setReady(true);

    if (stored) {
      setConsent(stored);
    }

    return scheduleConsentReplay(stored, applyConsent);
  }, []);

  // Изчистваме висящите sweep таймери при unmount.
  useEffect(
    () => () => {
      sweepTimersRef.current.forEach((id) => window.clearTimeout(id));
      sweepTimersRef.current = [];
    },
    [],
  );

  // Записва + прилага избора и затваря настройките/банера.
  const commit = useCallback((next) => {
    const full = {
      necessary: true,
      analytics: Boolean(next.analytics),
      marketing: Boolean(next.marketing),
    };

    // Четем предишното състояние ПРЕДИ saveConsent да го презапише. localStorage е
    // верният източник (издържа reload и втори таб), затова dep array-ът остава [].
    const prev = getStoredConsent();
    const revokedAnalytics = Boolean(prev?.analytics) && !full.analytics;
    const revokedMarketing = Boolean(prev?.marketing) && !full.marketing;

    saveConsent(full);
    // Редът е задължителен: първо denied към Consent Mode/GTM (за да гръмнат revoke
    // таговете), чак после триене на бисквитките.
    applyConsent(full);

    if (revokedAnalytics || revokedMarketing) {
      const sweep = () => {
        if (revokedAnalytics) {
          clearConsentCookies('analytics');
        }
        if (revokedMarketing) {
          clearConsentCookies('marketing');
        }
      };
      sweep();
      COOKIE_SWEEP_RETRY_DELAYS_MS.forEach((delay) => {
        sweepTimersRef.current.push(window.setTimeout(sweep, delay));
      });
    }

    setConsent(full);
    setSettingsOpen(false);
  }, []);

  const acceptAll = useCallback(() => commit(ACCEPTED_CONSENT), [commit]);
  const rejectAll = useCallback(() => commit(REJECTED_CONSENT), [commit]);
  const saveSettings = useCallback((partial) => commit(partial), [commit]);
  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo(
    () => ({
      consent,
      ready,
      settingsOpen,
      hasDecided: consent !== null,
      acceptAll,
      rejectAll,
      saveSettings,
      openSettings,
      closeSettings,
    }),
    [consent, ready, settingsOpen, acceptAll, rejectAll, saveSettings, openSettings, closeSettings],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return ctx;
}
